<template>
  <div id="modal" ref="crosswalk" :style="cssProps">
    <div id="modal-content">
      <div id="close" v-on:click="$emit('modal-close')">&times;</div>
      <div id="modalHeader">
        <div id="modalHeaderTopRow">
          <div id="leftMarker">
            <svg viewBox="-11.67 0 70 70" width="70" height="70">
              <use xlink:href="#myMarker"/>
            </svg>
          </div>
          <div id="rightMarker">
            <svg viewBox="-11.67 0 70 70" width="70" height="70">
              <use xlink:href="#myMarker"/>
            </svg>
          </div>
          <div id="stackedTopHeaderText">
            <div id="riskFactorText">{{ riskLevelText }}</div>
            <div id="riskRankingText">1 of {{ riskCount }} in Hingham, MA</div>
          </div>
        </div>
      </div>
      <div id="modalBody">
        <div id="modalBodyTitle">
          <div class="line2" id="topTitleRule"/>
          <div id="crosswalkTitleContainer">
            <div v-if="!isEditMode" id="crosswalkTitle">{{ crosswalk.displayName }}</div>
            <input v-else v-model="crosswalk.displayName" id="crosswalkTitleEditor">
          </div>
          <div class="line2" id="bottomTitleRule" />
        </div>
        <div id="modalBodyContent">
          <div id="linksBar">
            <span class="linkBarItem firstItem isButton">
              <a v-on:click="handleReportClick()">
                <AutoVector
                  width="20px"
                  height="20px"
                  class="linkBarIcon widerIcon"/>Report Driver
                </a>
            </span>
            <span class="linkBarItem isButton">
              <a v-on:click="handleCorrectionClick()">
                <PersonBubbleVector
                  width="16px"
                  height="16px"
                  title="Submit a Correction"
                  class="linkBarIcon"/>Add Correction
              </a>
            </span>
            <span class="linkBarItem isButton"
              v-if="isAdmin && !isEditMode"
              v-on:click="toggleEditMode()">
                <PencilVector width="16px" height="16px" class="linkBarIcon"/>Edit
            </span>
            <span v-else-if="isAdmin">
              <a v-on:click="crosswalkUpdateCancel()">
                <span class="linkBarItem isButton">
                  <CancelVector
                    width="18px"
                    height="18px"
                    class="linkBarIcon widerIcon"/>Cancel
                </span>
              </a>
              <a v-on:click="crosswalkUpdateSave()">
                <span class="linkBarItem isButton">
                  <SaveVector
                    width="18px"
                    height="18px"
                    class="linkBarIcon"/>Save
                </span>
              </a>
            </span>
            <span v-else-if="!isAuthenticated"
              class="linkBarItem isButton"
              v-on:click="login()">
              <LoginVector
                width="16px"
                height="16px"
                title="Login with a Google or Facebook Account"
                class="linkBarIcon"/>Sign In
            </span>
          </div>
          <div class="composer" v-if="isComposingCorrection">
            <div class="composerBkg"/>
            <div class="composerBodyContainer">
              <div class="composerBody">
                <h3 class="formHeading">Did we get something wrong?</h3>
                <div>Send us a correction about this crosswalk and we'll get it fixed!</div>
                <div id="correctionTextareaLabel">Correction Details</div>
                <textarea
                  id="correctionTextarea"
                  v-model="correction.correctionText"
                  :placeholder="'Tell us about this crosswalk at ' + this.crosswalk.displayName"
                  rows="5"
                  required>
                </textarea>
                <div class="composerButtons">
                  <a v-on:click="toggleComposingCorrection()">
                    <span class="linkBarItem isButton">
                      <CancelVector
                        width="18px"
                        height="18px"
                        class="linkBarIcon widerIcon"/>Cancel
                    </span>
                  </a>
                  <a v-on:click="correctionSend()">
                    <span class="linkBarItem isButton">
                      <SendVector
                        width="18px"
                        height="18px"
                        class="linkBarIcon"/>Send
                    </span>
                  </a>
                </div>
              </div>
            </div>
          </div>
          <div class="composer" v-if="isComposingReport">
            <div class="composerBkg"/>
            <div class="composerBodyContainer">
              <div class="composerBody">
                <h3 class="formHeading">Driver failed to yield? Other infraction?</h3>
                <div>Report it and we'll compile into a weekly summary for the
                traffic committe. Please note your name and report details
                will be publicly available.</div>
                <div class="inputLabel">Time of Violation</div>
                <input
                  class="inputDate"
                  v-model="report.violationTimestamp"
                  required/>
                <div class="inputLabel">Incident Summary</div>
                <textarea
                  id="reportTextarea"
                  v-model="report.summaryText"
                  :placeholder="'Please describe what happened at the crosswalk at ' +
                    this.crosswalk.displayName"
                  rows="5"
                  required>
                </textarea>
                <div class="composerButtons">
                  <a v-on:click="toggleComposingReport()">
                    <span class="linkBarItem isButton">
                      <CancelVector
                        width="18px"
                        height="18px"
                        class="linkBarIcon widerIcon"/>Cancel
                    </span>
                  </a>
                  <a v-on:click="reportSend()">
                    <span class="linkBarItem isButton">
                      <SendVector
                        width="18px"
                        height="18px"
                        class="linkBarIcon"/>Send
                    </span>
                  </a>
                </div>
              </div>
            </div>
          </div>
          <div id="crosswalkDetailMap">
            <div id="modalStatus" v-if="showStatus">
                {{ statusMessage }}
                <span id="statusClose" v-on:click="showStatus = false">X</span>
            </div>
            <CrosswalkDetailMap
               :crosswalk="crosswalk"
            />
          </div>
          <template v-if="isEditMode">
            <CrosswalkModalBodyEditor
              :crosswalk="crosswalk"
            />
          </template>
          <template v-else>
            <CrosswalkModalBody
              :crosswalk="crosswalk"
            />
          </template>
        </div>
      </div>
    </div>
    <svg class="defs-only" xmlns="http://www.w3.org/2000/svg"><title>bordered-marker</title><g id="myMarker"><g id="marker"><path class="cls-1" d="M16.11,36.12c-.33-.5-.65-1-1-1.64a22.83,22.83,0,1,1,39.78,0l-.16.24-.16.28c-.22.37-.42.74-.66,1.09L35,69ZM35,11.17A12.17,12.17,0,1,0,47.17,23.33,12.18,12.18,0,0,0,35,11.17Z" transform="translate(-11.67)"/><path class="cls-2" d="M35,1A22.28,22.28,0,0,1,54.47,34.2l-.16.24,0,0v0l-.15.25c-.21.37-.41.73-.64,1.06l0,0v0L35,68,16.54,35.87v0l0,0c-.31-.46-.62-1-1-1.57A22.28,22.28,0,0,1,35,1m0,35A12.67,12.67,0,1,0,22.33,23.33,12.69,12.69,0,0,0,35,36M35,0A23.29,23.29,0,0,0,14.67,34.73c.32.56.65,1.11,1,1.64L35,70,54.32,36.37c.3-.44.56-.91.82-1.37l.19-.27A23.29,23.29,0,0,0,35,0Zm0,35A11.67,11.67,0,1,1,46.67,23.33,11.67,11.67,0,0,1,35,35Z" transform="translate(-11.67)"/></g><g id="ring"><path class="cls-3" d="M35,37.54A14.21,14.21,0,1,1,49.21,23.33,14.23,14.23,0,0,1,35,37.54Zm0-23.33a9.13,9.13,0,1,0,9.12,9.12A9.13,9.13,0,0,0,35,14.21Z" transform="translate(-11.67)"/><path class="cls-2" d="M35,9.5A13.84,13.84,0,1,1,21.17,23.33,13.85,13.85,0,0,1,35,9.5m0,23.33a9.5,9.5,0,1,0-9.5-9.5,9.51,9.51,0,0,0,9.5,9.5M35,8.75A14.59,14.59,0,1,0,49.58,23.33,14.58,14.58,0,0,0,35,8.75Zm0,23.33a8.75,8.75,0,1,1,8.75-8.75A8.75,8.75,0,0,1,35,32.08Z" transform="translate(-11.67)"/></g></g></svg>
  </div>
</template>
<script>

import axios from 'axios';
import EventBus from '@/event-bus';
import CrosswalkDetailMap from '@/components/CrosswalkDetailMap.vue';
import CrosswalkModalBody from '@/components/CrosswalkModalBody.vue';
import CrosswalkModalBodyEditor from '@/components/CrosswalkModalBodyEditor.vue';
import PencilVector from '@/components/icons/PencilVector.vue';
import AutoVector from '@/components/icons/AutoVector.vue';
import PersonBubbleVector from '@/components/icons/PersonBubbleVector.vue';
import SaveVector from '@/components/icons/SaveVector.vue';
import CancelVector from '@/components/icons/CancelVector.vue';
import SendVector from '@/components/icons/SendVector.vue';
import LoginVector from '@/components/icons/LoginVector.vue';
import { isInGroup } from '../user';

export default {

  components: {
    CrosswalkDetailMap,
    CrosswalkModalBody,
    CrosswalkModalBodyEditor,
    PencilVector,
    AutoVector,
    PersonBubbleVector,
    SaveVector,
    CancelVector,
    SendVector,
    LoginVector,
  },

  data() {
    return {
      isAuthenticated: null,
      isAdmin: false,
      isEditMode: false,
      isComposingCorrection: false,
      isComposingReport: false,
      showStatus: false,
      correction: {},
      report: {},
      statusMessage: '<statusMessage>',
      crosswalk: null,
    };
  },

  props: {
    permCrosswalk: Object,
    riskCount: Number,
  },

  async mounted() {
    this.isAuthenticated = await this.$auth.isAuthenticated();
  },

  created() {
    // eslint-disable-next-line
    isInGroup('Admin').then((response) => (this.isAdmin = response));
    this.crosswalk = JSON.parse(JSON.stringify(this.permCrosswalk));
    this.report.violationTimestamp = new Date();
  },

  computed: {
    cssProps() {
      let accentColor = '#D1D1D1';
      let markerColor = '#FFFFFF';
      if (Number(this.crosswalk.riskLevel) === 2) {
        accentColor = '#FED200';
        markerColor = '#FFDD07';
      } else if (Number(this.crosswalk.riskLevel) === 1) {
        accentColor = '#C0392B';
        markerColor = '#F24C3B';
      }
      return {
        '--accent-color': accentColor,
        '--marker-color': markerColor,
      };
    },
    riskLevelText() {
      let txt = 'LOW RISK';
      if (Number(this.crosswalk.riskLevel) === 2) {
        txt = 'MODERATE RISK';
      } else if (Number(this.crosswalk.riskLevel) === 1) {
        txt = 'HIGH RISK';
      }
      return txt;
    },
  },

  methods: {
    toggleEditMode() {
      this.isEditMode = !this.isEditMode;
      if (this.isEditMode === true) {
        this.$markerIndex[this.crosswalk._id][2].setDraggable(true);
        this.$markerIndex[this.crosswalk._id][3].setVisible(false);
      } else {
        this.$markerIndex[this.crosswalk._id][2].setDraggable(false);
        this.$markerIndex[this.crosswalk._id][3].setVisible(true);
      }
    },

    handleCorrectionClick() {
      if (!this.isAuthenticated) {
        this.statusMessage = 'To submit a correction please login';
        this.showStatus = true;
      } else {
        this.showStatus = false;
        this.toggleComposingCorrection();
      }
    },

    toggleComposingCorrection() {
      this.isComposingCorrection = !this.isComposingCorrection;
    },

    handleReportClick() {
      if (!this.isAuthenticated) {
        this.statusMessage = 'To submit a driver report please login';
        this.showStatus = true;
      } else {
        this.showStatus = false;
        this.toggleComposingReport();
      }
    },

    toggleComposingReport() {
      this.isComposingReport = !this.isComposingReport;
    },

    crosswalkUpdateCancel() {
      const pos = this.$markerIndex[this.crosswalk._id][3].getPosition();
      this.$markerIndex[this.crosswalk._id][2].setPosition(pos);
      this.toggleEditMode();
      this.crosswalk = JSON.parse(JSON.stringify(this.permCrosswalk));
    },

    login() {
      this.$auth.loginRedirect();
    },

    async crosswalkUpdateSave() {
      const accessToken = await this.$auth.getAccessToken();
      const pos = this.$markerIndex[this.crosswalk._id][2].getPosition();
      this.crosswalk.lat = pos.lat();
      this.crosswalk.lng = pos.lng();
      const client = axios.create({
        baseURL: 'https://7yt2za0ijd.execute-api.us-east-1.amazonaws.com/dev/',
        // baseURL: 'http://localhost:8081',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      const { data } = await client.put('/crosswalks', this.crosswalk);
      this.data = data;
      // move all the markers to the new coordinates
      this.$markerIndex[this.crosswalk._id][0].setPosition(pos);
      this.$markerIndex[this.crosswalk._id][1].setPosition(pos);
      this.$markerIndex[this.crosswalk._id][3].setPosition(pos);
      // make sure the marker colors are set correctly
      let accentColor = '#D1D1D1';
      let markerColor = '#FFFFFF';
      if (Number(this.crosswalk.riskLevel) === 2) {
        accentColor = '#FED200';
        markerColor = '#FFDD07';
      } else if (Number(this.crosswalk.riskLevel) === 1) {
        accentColor = '#C0392B';
        markerColor = '#F24C3B';
      }
      this.$markerIndex[this.crosswalk._id].forEach((m, index) => {
        if (m.shape === 'marker') {
          m.icon.fillColor = markerColor;
        } else if (m.shape === 'ring') {
          m.icon.fillColor = accentColor;
        }
        const map = m.getMap();
        if (index < 2) m.marker.priority = this.crosswalk.riskLevel.toString();
        m.setMap(null);
        m.setMap(map);
      });
      // tell everyone it's done
      EventBus.$emit('crosswalk-save', this.crosswalk);
      this.toggleEditMode();
      this.statusMessage = 'Updates successfully applied';
      this.showStatus = true;
    },

    async correctionSend() {
      const accessToken = await this.$auth.getAccessToken();
      const client = axios.create({
        baseURL: 'https://7yt2za0ijd.execute-api.us-east-1.amazonaws.com/dev/',
        // baseURL: 'http://localhost:8081',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      this.correction.crossingId = this.crosswalk._id;
      // todo: fix this reference
      if (this.$parent.$parent.activeUser) {
        this.correction.username = this.$parent.$parent.activeUser.email;
      } else this.correction.username = 'anonymous';
      const { data } = await client.post('/corrections', this.correction);
      this.data = data;
      console.log(data);
      this.toggleComposingCorrection();
      this.correction = {};
      this.statusMessage = "Thanks!! We'll look into it!";
      this.showStatus = true;
    },

    async reportSend() {
      const accessToken = await this.$auth.getAccessToken();
      const client = axios.create({
        baseURL: 'https://7yt2za0ijd.execute-api.us-east-1.amazonaws.com/dev/',
        // baseURL: 'http://localhost:8081',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      // const { data } = await client.get('/secure-data');
      this.report.crossingId = this.crosswalk._id;
      // todo: fix this reference
      if (this.$parent.$parent.activeUser) {
        this.report.username = this.$parent.$parent.activeUser.email;
      } else this.report.username = 'anonymous';
      const { data } = await client.post('/reports', this.report);
      this.data = data;
      console.log(data);
      this.toggleComposingReport();
      this.report = {};
      this.report.violationTimestamp = new Date();
      this.statusMessage = 'Thanks!! Your report will be sent to the traffic committee.';
      this.showStatus = true;
    },
  },
};
</script>

<style>

body {font-family: Arial, Helvetica, sans-serif;}

@media only screen and (max-width: 450px) {
  #crosswalkTitleContainer {
    font-size: 1.2em !important;
  }

  #modal {
    padding-top: 8px !important;
    padding-bottom: 0px !important;
  }
}

@media only screen and (max-height: 875px) {
  #crosswalkDetailMap {
    height: 220px !important;
  }
  #modal-content {
    height: 700px !important;
  }
  #modalBody {
    height: 580px !important;
  }
}

#modal {
  display: visible;
  position: fixed;
  z-index: 1;
  padding-top: 50px;
  padding-bottom: 50px;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  overflow: hidden;
}

#modal-content {
  position: relative;
  background-color: #fefefe;
  margin-right: auto;
  margin-left: auto;
  padding: 0px;
  border: 5px solid var(--accent-color);
  width: 95%;
  max-width: 500px;
  min-width: 375px;
  height: 800px;
  border-radius: 24px;
}

#close {
    position: absolute;
    z-index: 2;
    margin: 0px;
    color: #aaaaaa;
    right: 8px;
    top: 0px;
    font-size: 28px;
    font-weight: bold;
}

#close:hover,
#close:focus {
  color: #000;
  text-decoration: none;
  cursor: pointer;
}

#modalHeader {
  width: 100%;
}

#modalHeaderTopRow {
  position: relative;
  height: 70px;
  padding-top: 32px;
  width: 100%;
}

#leftMarker {
  position: absolute;
  left: 8px;
  height: 70px;
  width: 70px
}
#rightMarker {
  position: absolute;
  right: 8px;
  height: 70px;
}
#stackedTopHeaderText {
  height: 70px;
}
#riskFactorText {
    font-size: 1.8em;
    font-weight: 600;
}

#topTitleRule {
  margin-left: 44px;
  margin-right: 44px;
  margin-top: 2px;
}

#bottomTitleRule {
  margin-left: 26px;
  margin-right: 26px;
}

#linksBar {
  margin: 12px 26px 8px 26px;
}

#modalStatus {
  position: absolute;
  margin: 20px 0px 0px 0px;
  width: 100%;
  background-color: #fae166;
  opacity: 0.9;
  z-index: 3;
  padding: 5px;
  font-weight: 500;
}

#statusClose {
  position: absolute;
  right: 26px;
  cursor: pointer;
}
#modalBody {
  position: relative;
  height: 682px;
  display: flex;
  flex-direction: column;
}

#modalBodyTitle {
}

#modalBodyContent {
  position: relative;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
}

.line2 {
  height: 2px;
  background:
    repeating-linear-gradient(
      to right,
      var(--accent-color) 0,
      var(--accent-color) 10px,
      transparent 10px,
      transparent 20px)
}

#crosswalkTitleContainer {
    font-weight: 800;
    font-size: 2.0em;
    padding-top: 12px;
    padding-bottom: 12px;
    margin-left: 26px;
    margin-right: 26px;
}

#crosswalkTitle {

}

.linkBarItem {
  padding-left: 16px;
  display: inline-block;
}

.linkBarIcon {
  display: inline-block;
  vertical-align: middle;
  padding-bottom: 4px;
  padding-right: 6px;
}

.isButton {
  cursor: pointer;
}

.disabled {
  cursor: auto;
  color: #aaa;
}

.widerIcon {
  padding-right: 4px !important;
}

.tallerIcon {
  padding-bottom: 2px !important;
}

.firstItem {
  padding-left: 0px !important;
}

#crosswalkTitleEditor {
  width: 95%;
  font-weight: 800;
  text-align: center;
}

#crosswalkDetailMap {
  padding-top: 0px;
  height: 290px
}

.formHeading {
  padding: 18px 0px 18px 0px;
  margin: 0px;
}

.composer {
  width: 100%;
  position: absolute;
  height: 100%;
}

.composerBodyContainer{
  width: 100%;
  position: absolute;
  height: 100%;
  z-index: 2;
}

.composerBody {
  margin: 46px 26px 20px 26px;
  text-align: left;
  z-index: 2;
}

.composerBkg {
  width: 100%;
  background-color: #fff;
  opacity: 0.9;
  position: absolute;
  height: 100%;
  z-index: 2;
}

.composerButtons {
  padding-top: 12px;
  text-align: right;
  width: 100%;
}

#correctionTextareaLabel {
  margin-top: 12px;
  margin-bottom: 6px;
  font-weight: 800;
}
#correctionTextarea {
  background-color: #fff;
  width: 100%;
  opacity: 0.8;
  padding: 6px;
}

.inputLabel {
  margin-top: 12px;
  margin-bottom: 6px;
  font-weight: 800;
}

.inputDate {
  width: 100%;
}

#reportTextarea {
  background-color: #fff;
  width: 100%;
  opacity: 0.8;
  padding: 6px;
}
.cls-1{
  fill: var(--marker-color);
}
.cls-2{
  fill: "#0f0";
}
.cls-3{
  fill: var(--accent-color);
}

input {
  font-size: 100%;
}

textarea {
  font-size: 100%;
}

select {
  font-size: 100%;
  font-family: Avenir, Arial, Helvetica, sans-serif;
}

svg.defs-only{
    display:block; position: absolute;
    height:0; width:0; margin: 0; padding: 0;
    border: none; overflow: hidden;
}
</style>
